import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Dropdown, Spin, message } from 'antd';
import { EllipsisOutlined, ReloadOutlined, ShareAltOutlined } from '@ant-design/icons';
import { countryInfoEn as copy } from '../../content/countryInfo.en';
import {
  fetchCountryInfoDetail,
  fetchCountryPorts,
  pickNearestPort,
  resolveCountryIso2,
  type CountryInfoDetailDto,
  type PortDto,
} from '../../services/countryInfoApi';
import { loadLocalUserPreferences } from '../../services/userPreferencesSyncService';
import { nationalityToIso2 } from '../../utils/nationalityOptions';
import { flagEmojiFromIso2 } from '../../utils/countryInfoLookups';
import {
  hasTripStarted,
  orderedCountryInfoSections,
  type CountryInfoSectionId,
} from '../../utils/countryInfoOrder';
import {
  CurrencyExchangeSection,
  EmbassySection,
  HealthSafetySection,
  KnowBeforeYouGoSection,
  PortInfoSection,
  RideHailSection,
  TippingSection,
  TimeZonePhoneSection,
} from './CountryInfoSections';
import styles from './CountryInfo.module.css';

export interface CountryInfoDrawerProps {
  open: boolean;
  onClose: () => void;
  cityName: string;
  countryISO2?: string | null;
  countryName?: string | null;
  currencyCode?: string | null;
  timeZoneIdentifier?: string | null;
  tripStartDate?: string | null;
  cityArrivalDate?: string | null;
  tripCurrency?: string | null;
  tripHomeTimeZone?: string | null;
  isCruisePort?: boolean;
  coords?: { latitude: number; longitude: number } | null;
  dockNumber?: string | null;
  portArrivalHm?: string | null;
  portDepartureHm?: string | null;
}

const CountryInfoDrawer: React.FC<CountryInfoDrawerProps> = ({
  open,
  onClose,
  cityName,
  countryISO2,
  countryName,
  currencyCode,
  timeZoneIdentifier,
  tripStartDate,
  cityArrivalDate,
  tripCurrency,
  tripHomeTimeZone,
  isCruisePort = false,
  coords,
  dockNumber,
  portArrivalHm,
  portDepartureHm,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<CountryInfoDetailDto | null>(null);
  const [port, setPort] = useState<PortDto | null>(null);
  const [citizenIso2, setCitizenIso2] = useState<string | null>(null);
  const [homeTz, setHomeTz] = useState('UTC');
  const [refreshKey, setRefreshKey] = useState(0);

  const iso2 = (countryISO2 || detail?.country.iso2 || '').trim().toUpperCase() || null;

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      const prefs = loadLocalUserPreferences();
      const nationality = nationalityToIso2(
        prefs?.appUserSettings.userNationality
      );
      setCitizenIso2(nationality);
      setHomeTz(
        prefs?.appUserSettings.homeTimeZone?.trim() ||
          tripHomeTimeZone?.trim() ||
          'UTC'
      );

      const resolved = await resolveCountryIso2({
        countryISO2,
        countryName,
        coords,
      });
      const code = resolved.code;
      if (!code) {
        setError(copy.errorLoad);
        setDetail(null);
        setPort(null);
        return;
      }

      const countryDetail = await fetchCountryInfoDetail(code, nationality);
      setDetail(countryDetail);

      if (isCruisePort) {
        const key = countryDetail.country.country || countryName || code;
        try {
          const ports = await fetchCountryPorts(key);
          setPort(pickNearestPort(ports, coords, cityName));
        } catch {
          setPort(null);
        }
      } else {
        setPort(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : copy.errorLoad);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [
    open,
    countryISO2,
    countryName,
    cityName,
    isCruisePort,
    coords,
    tripHomeTimeZone,
  ]);

  useEffect(() => {
    if (open) {
      void load();
    }
  }, [open, load, refreshKey]);

  const started = hasTripStarted(new Date(), tripStartDate, cityArrivalDate);
  const sections = orderedCountryInfoSections(started, { includePort: isCruisePort });

  const destCurrency =
    detail?.country.currencyCode ||
    detail?.currency?.currencyCode ||
    currencyCode ||
    null;
  const homeCurrency = useMemo(() => {
    // Prefer trip currency when nationality currency is unknown; embassy citizen
    // currency can be refined later via dataset. Match iOS fallback: trip.currency.
    return tripCurrency?.trim().toUpperCase() || destCurrency;
  }, [tripCurrency, destCurrency]);

  const destTz =
    timeZoneIdentifier && timeZoneIdentifier !== 'America/Chicago'
      ? timeZoneIdentifier
      : tripHomeTimeZone || 'UTC';

  const onShare = async () => {
    const text = `${cityName} — ${detail?.country.country || countryName || iso2 || ''}`.trim();
    try {
      if (navigator.share) {
        await navigator.share({ title: copy.title, text });
      } else {
        await navigator.clipboard.writeText(text);
        message.success('Copied');
      }
    } catch {
      /* user cancelled */
    }
  };

  const renderSection = (id: CountryInfoSectionId) => {
    if (!detail) return null;
    switch (id) {
      case 'knowBefore':
        return (
          <KnowBeforeYouGoSection
            key={id}
            country={detail.country}
            citizenIso2={citizenIso2}
          />
        );
      case 'health':
        return <HealthSafetySection key={id} country={detail.country} />;
      case 'embassy':
        return (
          <EmbassySection
            key={id}
            embassies={detail.embassies || []}
            hasNationality={Boolean(citizenIso2)}
          />
        );
      case 'currency':
        return (
          <CurrencyExchangeSection
            key={id}
            homeCurrency={homeCurrency}
            destCurrency={destCurrency}
            destCurrencyName={detail.country.currencyName}
            refreshKey={refreshKey}
          />
        );
      case 'tipping':
        return <TippingSection key={id} iso2={detail.country.iso2} />;
      case 'rideHail':
        return (
          <RideHailSection key={id} companiesRaw={detail.country.rideHailCompanies} />
        );
      case 'port':
        return (
          <PortInfoSection
            key={id}
            port={port}
            dockNumber={dockNumber}
            arrivalHm={portArrivalHm}
            departureHm={portDepartureHm}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Drawer
      title={copy.title}
      placement="bottom"
      height="92%"
      open={open}
      onClose={onClose}
      className={styles.drawer}
      extra={
        <div className={styles.toolbarExtra}>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'share',
                  icon: <ShareAltOutlined />,
                  label: copy.share,
                  onClick: () => void onShare(),
                },
                {
                  key: 'refresh',
                  icon: <ReloadOutlined />,
                  label: copy.refresh,
                  onClick: () => setRefreshKey((k) => k + 1),
                },
              ],
            }}
            trigger={['click']}
          >
            <Button type="text" icon={<EllipsisOutlined />} aria-label="More" />
          </Dropdown>
          <Button type="link" onClick={onClose}>
            {copy.done}
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className={styles.centerState}>
          <Spin />
          <p className={styles.muted}>{copy.loading}</p>
        </div>
      ) : error && !detail ? (
        <div className={styles.centerState}>
          <p className={styles.muted}>{error}</p>
          <Button type="primary" onClick={() => setRefreshKey((k) => k + 1)}>
            {copy.retry}
          </Button>
        </div>
      ) : detail ? (
        <div className={styles.scroll}>
          <header className={styles.headerCard}>
            <div className={styles.flag} aria-hidden>
              {flagEmojiFromIso2(detail.country.iso2 || iso2)}
            </div>
            <h2 className={styles.cityName}>{cityName}</h2>
          </header>
          <TimeZonePhoneSection
            homeTz={homeTz}
            destTz={destTz}
            phoneCode={detail.country.phoneCode}
          />
          {sections.map((id) => renderSection(id))}
        </div>
      ) : (
        <div className={styles.centerState}>
          <p className={styles.muted}>{copy.errorLoad}</p>
        </div>
      )}
    </Drawer>
  );
};

export default CountryInfoDrawer;
